import { FluentProvider } from '@fluentui/react-components';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { StrictMode, lazy, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router';

import { Shell } from '@/components/layout/Shell';
import { LoadingState, PermissionDenied } from '@/components/ui/States';
import { ToastProvider } from '@/components/ui/Toast';
import { AuthProvider, useAuth } from '@/hooks/useAuth';
import { syaaLightTheme } from '@/theme';

import './styles/index.css';

const Login = lazy(() => import('@/pages/Login'));
const Overview = lazy(() => import('@/pages/Overview'));
const Messages = lazy(() => import('@/pages/Messages'));
const Recipients = lazy(() => import('@/pages/Recipients'));
const Templates = lazy(() => import('@/pages/Templates'));
const Audit = lazy(() => import('@/pages/Audit'));
const Docs = lazy(() => import('@/pages/Docs'));
const ApiKeys = lazy(() => import('@/pages/Settings').then((m) => ({ default: m.ApiKeysPage })));
const Users = lazy(() => import('@/pages/Settings').then((m) => ({ default: m.UsersPage })));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 15_000,
      retry: (count, error) => {
        // Never retry an auth or permission failure — it will not succeed.
        const status = (error as { status?: number }).status;
        if (status === 401 || status === 403) return false;
        return count < 2;
      },
      refetchOnWindowFocus: false,
    },
  },
});

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { user, initialising } = useAuth();
  const location = useLocation();

  if (initialising) return <LoadingState label="Restoring session" />;
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  return <>{children}</>;
}

function RequireAdmin({ children }: { children: React.ReactNode }) {
  const { isAdmin } = useAuth();
  // Guarded here for UX; the backend enforces it for real.
  return isAdmin ? <>{children}</> : <PermissionDenied />;
}

function App() {
  return (
    <Suspense fallback={<LoadingState />}>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route
          element={
            <RequireAuth>
              <Shell />
            </RequireAuth>
          }
        >
          <Route index element={<Overview />} />
          <Route path="messages" element={<Messages />} />
          <Route path="recipients" element={<Recipients />} />
          <Route path="templates" element={<Templates />} />
          <Route path="docs" element={<Docs />} />
          <Route
            path="settings/api-keys"
            element={
              <RequireAdmin>
                <ApiKeys />
              </RequireAdmin>
            }
          />
          <Route
            path="settings/users"
            element={
              <RequireAdmin>
                <Users />
              </RequireAdmin>
            }
          />
          <Route
            path="audit"
            element={
              <RequireAdmin>
                <Audit />
              </RequireAdmin>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </Suspense>
  );
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    {/* Light theme only — the portal default, and what was asked for. */}
    <FluentProvider theme={syaaLightTheme} style={{ height: '100%' }}>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AuthProvider>
            <ToastProvider>
              <App />
            </ToastProvider>
          </AuthProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </FluentProvider>
  </StrictMode>,
);
